'use client'

import {Label} from '../label'
import {Checkbox as AntCheckBox, CheckboxChangeEvent, CheckboxProps, Tooltip} from 'antd'
import {CircleHelp} from 'lucide-react'
import React, {useEffect, useState} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import styled, {css} from 'styled-components'

import theme from '@/src/config/theme'

import InputTooltip from '@/src/shared/ui/inputs/tooltip'

export interface IProps extends Omit<
  CheckboxProps & IInputProps & React.PropsWithChildren,
  'name'
> {
  name: string
}

export const FormCheckBox = (props: IProps) => {
  const {name, children, tooltip, className} = props

  // Генерация уникального ID
  const [id] = useState(() => `checkbox-${name}`)

  // Подключение к React Hook Form
  const formControl = useFormContext()
  const {field, fieldState} = useController({name, control: formControl.control})

  return (
    <CheckBox
      {...props}
      id={id}
      checked={field.value ?? false}
      onChange={checked => {
        field.onChange(checked)
      }}
      tooltip={tooltip}
      error={fieldState.error?.message}
      className={className}>
      {children}
    </CheckBox>
  )
}

export const CheckBox = (
  props: {
    id: string
    name: string
    disabled?: boolean
    defaultChecked?: boolean
    checked?: boolean
    onChange: (checked: boolean) => void
    tooltip?: string
    error?: string
    className?: string
  } & React.PropsWithChildren,
) => {
  const {id, defaultChecked, checked, onChange, children, tooltip, error, disabled, className} =
    props

  const [isChecked, setIsChecked] = useState(checked ?? defaultChecked ?? false)

  const onChangeByClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const newChecked = !checked
    onChangeHandler(newChecked)
  }

  const onChangeByInput = (e: CheckboxChangeEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const newChecked = e.target.checked
    onChangeHandler(newChecked)
  }

  const onChangeHandler = (newChecked: boolean) => {
    setIsChecked(newChecked)
    if (onChange) {
      onChange(newChecked)
    }
  }

  useEffect(() => {}, [])

  return (
    <Wrapper
      className={className}
      $error={!!error}
      onClick={onChangeByClick}
      $disabled={disabled ?? false}>
      <AntCheckBox
        id={id}
        name={props.name}
        disabled={disabled}
        checked={checked !== undefined ? checked : isChecked}
        onChange={onChangeByInput}>
        {children && <Label htmlFor={id}>{children}</Label>}
        {tooltip && <InputTooltip title={tooltip} />}
        {error && <CheckBoxErrorToolTip title={error} />}
      </AntCheckBox>
    </Wrapper>
  )
}

const CheckBoxErrorToolTip = ({title}: {title: string}) => {
  return (
    <Tooltip placement="bottomRight" title={title} showArrow>
      <CircleHelp color={theme.colors.error} size={36} className="checkbox-error" />
    </Tooltip>
  )
}

export const Wrapper = styled.div<{$error: boolean; $disabled: boolean}>`
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    cursor: pointer;
  }

  ${props =>
    props.$disabled &&
    css`
      pointer-events: none;
    `}

  ${props =>
    props.$error &&
    css`
      .ant-checkbox-inner {
        border-color: ${props.theme.colors.error};
      }
    `};

  .ant-checkbox-label {
    display: flex;
    align-items: center;
  }

  .checkbox-error {
    margin-left: 6px;
  }
  //& .ant-checkbox,
  //& .ant-checkbox-inner {
  //  width: 20px;
  //  height: 20px;
  //}
`
